import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, useTenant, type DomainSwapOut } from '../../lib/api';

/** Customer-facing domain swap. Same operation as the operator page,
 * trimmed warning copy ("Destructive. Old addresses disappear."). Backend
 * enforces client_id matching, so a customer can only swap a domain on
 * a tenant they actually own. */
export default function UserDomainSwap() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: t } = useTenant(id);
  const [to, setTo] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DomainSwapOut | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!t) return null;
  const from = t.target_domain;

  async function go() {
    if (confirm !== to) { setErr('type the new domain to confirm'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const out = await api.domainSwap(id, { from_domain: from, to_domain: to });
      setResult(out);
      qc.invalidateQueries({ queryKey: ['tenant', id] });
    } catch (e: any) {
      setErr(e?.message || 'failed to start swap');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 max-w-xl">
      <div className="card p-5 space-y-3">
        <div className="text-xs text-warn">
          Destructive. Old addresses disappear.
        </div>
        <div className="block">
          <div className="text-xs text-dim mb-1">From domain</div>
          <div className="input bg-bg text-dim cursor-not-allowed">{from}</div>
        </div>
        <label className="block">
          <div className="text-xs text-dim mb-1">To domain</div>
          <input
            className="input" value={to}
            onChange={e => setTo(e.target.value.trim().toLowerCase())}
            placeholder="newdomain.com"
            autoComplete="off"
          />
        </label>
        <div className="text-[12px] text-dim italic">
          The old domain will be detached from the tenant after the swap. Old addresses disappear,
          and every mailbox re-authenticates to your sequencer automatically under its new address.
        </div>
        <label className="block">
          <div className="text-xs text-dim mb-1">Type <code className="text-ink">{to || 'the new domain'}</code> to confirm</div>
          <input className="input" value={confirm} onChange={e => setConfirm(e.target.value)} />
        </label>
        {err && <div className="text-err text-xs">{err}</div>}
        <button
          onClick={go}
          disabled={busy || !to || confirm !== to || to === from}
          className="btn-danger"
        >
          {busy ? 'Starting…' : 'Run swap'}
        </button>
      </div>

      {result && (
        <div className="card p-5 mt-4 space-y-3 border border-accent/30">
          <div className="text-xs uppercase tracking-wider text-dim">
            {result.ns_status === 'active' ? 'Swap queued' : 'Awaiting nameserver delegation'}
          </div>
          {result.ns_status === 'active' ? (
            <div className="text-sm text-ink">
              Cloudflare already shows <span className="font-mono">{to}</span> as active.
              The swap will run in the background.
            </div>
          ) : (
            <>
              <div className="text-sm text-ink">
                At your domain registrar, replace the existing nameservers for{' '}
                <span className="font-mono">{to}</span> with the two below. The swap
                runs automatically once delegation is confirmed.
              </div>
              <div className="space-y-2">
                {result.nameservers.map(ns => (
                  <div key={ns} className="flex items-center justify-between gap-3 rounded-md bg-white/[0.03] px-3 py-2 font-mono text-sm">
                    <span className="truncate">{ns}</span>
                    <button
                      onClick={() => navigator.clipboard?.writeText(ns)}
                      className="text-xs text-dim hover:text-ink"
                      title="Copy"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
